import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Calendar, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOW_OPTIONS = [
  { value: 'morning', label: 'Morning (8:30 AM – 11:00 AM)' },
  { value: 'midday', label: 'Midday (11:00 AM – 1:30 PM)' },
  { value: 'afternoon', label: 'Afternoon (1:30 PM – 3:30 PM)' },
  { value: 'evening', label: 'Late Afternoon (3:30 PM – 5:00 PM)' },
];

const Reschedule = () => {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ date: '', time_window: 'morning' });
  const [blocked, setBlocked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/appointments/reschedule/${token}`)
      .then(({ data }) => {
        setAppt(data);
        setForm({ date: data.date || '', time_window: data.time_window || 'morning' });
      })
      .catch((e) => setErr(e.response?.data?.detail || 'This reschedule link is invalid or expired.'))
      .finally(() => setLoading(false));
    axios.get(`${API}/blocked-dates`).then(({ data }) => {
      if (Array.isArray(data)) setBlocked(data);
      else setBlocked(data?.dates || []);
    }).catch((err) => console.error('Failed to fetch blocked dates:', err));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (blocked.some((b) => b.date === form.date)) {
      setErr('That date is unavailable. Please pick another.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/appointments/reschedule/${token}`, form);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not update the appointment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#faf6f0] py-16 px-6">
      <div className="max-w-[720px] mx-auto">
        <div className="text-center mb-8">
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold mb-3">CLIENT PORTAL</p>
          <h1 className="font-serif text-4xl text-[#1c1e20]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Reschedule your appointment
          </h1>
          <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mt-3" />
        </div>

        {loading && <p className="text-center text-[#6b6b6b]">Loading your appointment…</p>}

        {!loading && !appt && (
          <div className="bg-white border border-black/5 p-10 text-center">
            <AlertCircle size={32} className="text-rose-500 mx-auto mb-3" />
            <p className="text-[#1c1e20] mb-4">{err || 'Invalid link.'}</p>
            <Link to="/contact" className="inline-block bg-[#c9a961] hover:bg-[#b89751] text-[#141618] tracking-[0.22em] text-[11px] font-semibold px-6 py-3 transition-colors">
              CONTACT US
            </Link>
          </div>
        )}

        {!loading && appt && done && (
          <div className="bg-white border border-[#c9a961] p-10 text-center">
            <CheckCircle2 size={36} className="text-[#c9a961] mx-auto mb-3" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl text-[#1c1e20] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              You&rsquo;re all set.
            </h2>
            <p className="text-[#4a4a4a]">Your appointment has been updated. A confirmation email is on its way.</p>
            <Link to="/" className="inline-block mt-6 border border-[#1c1e20] hover:bg-[#1c1e20] hover:text-white text-[#1c1e20] tracking-[0.22em] text-[11px] font-semibold px-6 py-3 transition-colors">
              RETURN HOME
            </Link>
          </div>
        )}

        {!loading && appt && !done && (
          <form onSubmit={submit} className="bg-white border border-black/5 p-8 md:p-10">
            <div className="mb-6 pb-6 border-b border-black/10">
              <p className="text-[10px] tracking-[0.28em] text-[#c9a961] font-semibold mb-2">CURRENT APPOINTMENT</p>
              <p className="text-[#1c1e20] font-semibold">{appt.name} · {appt.service}</p>
              <p className="text-[#6b6b6b] text-[13px] mt-1">
                <Calendar size={13} className="inline mr-1 -mt-0.5 text-[#c9a961]" />
                {appt.date || 'Flexible date'} · {appt.time_window || '—'}
              </p>
            </div>

            <label className="block text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-2">NEW DATE</label>
            <input
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full border border-black/15 px-4 py-3 mb-5 focus:outline-none focus:border-[#c9a961]"
            />

            <label className="block text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-2">NEW TIME WINDOW</label>
            <select
              value={form.time_window}
              onChange={(e) => setForm({ ...form, time_window: e.target.value })}
              className="w-full border border-black/15 px-4 py-3 mb-5 bg-white focus:outline-none focus:border-[#c9a961]"
            >
              {WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {err && <p className="text-rose-600 text-sm mb-3">{err}</p>}

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-[#c9a961] hover:bg-[#b89751] transition-colors text-[#141618] tracking-[0.22em] text-[12px] font-semibold py-4 disabled:opacity-60"
            >
              {saving ? 'UPDATING…' : 'CONFIRM RESCHEDULE'}
            </button>
            <p className="text-[11px] text-[#6b6b6b] mt-3 text-center">Available Sunday through Saturday, 7:00 AM – 8:00 PM.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Reschedule;
